// Shared display helpers for chat action cards. Used by both
// `ActionCard` (single tool call) and `BatchActionCard` (consecutive
// same-tool tool calls grouped into one card).

const TOOL_LABELS: Record<string, string> = {
  create_share: 'Creating share',
  update_share: 'Updating share',
  delete_share: 'Deleting share',
  search_assets: 'Searching assets',
  get_asset_info: 'Getting asset info',
  upload_asset: 'Uploading asset',
  replace_asset: 'Replacing asset',
  update_metadata: 'Updating metadata',
  tag_assets_bulk: 'Tagging assets',
  remove_background: 'Removing background',
  get_viewable_url: 'Getting preview URL',
  get_thumbnail: 'Getting thumbnail',
  get_asset_download_url: 'Getting download URL',
  get_social_media_images: 'Generating social media images',
  get_branded_image_urls: 'Generating branded image URLs',
  print_export: 'Making print-ready file',
  generate_campaign_links: 'Generating campaign links',
  stream_video: 'Loading video',
  watermark_image_assets_with_image_overlay: 'Applying watermark',
  watermark_image_assets_with_text_overlay: 'Applying text watermark',
  transcode_video: 'Transcoding video',
  create_video_clip: 'Creating video clip',
  search_collections: 'Searching collections',
  get_collection: 'Getting collection',
  create_collection: 'Creating collection',
  update_collection: 'Updating collection',
  update_collection_assets: 'Updating collection assets',
  get_collection_download_url: 'Getting collection download',
  get_share_info: 'Getting share info',
  search_shares: 'Searching shares',
  list_shares: 'Listing shares',
  get_share_analytics: 'Loading analytics',
  get_share_approvals: 'Checking approvals',
  list_share_templates: 'Loading templates',
  search_by_face: 'Searching by face',
  get_auto_tags: 'Getting auto tags',
  enhance_image: 'Enhancing image',
  upscale_image: 'Upscaling image',
  remove_object: 'Removing object',
  generate_image: 'Generating image',
  generative_fill: 'Generative fill',
  generative_expand: 'Generative expand',
  tag_image: 'AI tagging image',
  rollback_metadata_update: 'Undoing metadata change',
};

export function getToolLabel(tool: string): string {
  return TOOL_LABELS[tool] || tool.replace(/_/g, ' ');
}

// Past-tense / batched label for a completed group of N same-tool calls.
// Falls back to the in-progress label when no specific past-tense exists.
const TOOL_PAST_LABELS: Record<string, string> = {
  remove_background: 'Removed background from {n} assets',
  update_metadata: 'Updated metadata on {n} assets',
  tag_assets_bulk: 'Tagged assets in {n} batches',
  transcode_video: 'Started transcoding {n} videos',
  create_video_clip: 'Created {n} video clips',
  search_assets: 'Ran {n} searches',
  get_asset_info: 'Loaded info for {n} assets',
  get_viewable_url: 'Loaded {n} preview URLs',
  get_thumbnail: 'Loaded {n} thumbnails',
  get_asset_download_url: 'Loaded {n} download URLs',
  get_branded_image_urls: 'Generated branded URLs for {n} assets',
  print_export: 'Made {n} print-ready files',
};

export function getBatchPastLabel(tool: string, n: number): string {
  const tpl = TOOL_PAST_LABELS[tool];
  if (tpl) return tpl.replace('{n}', String(n));
  return `${getToolLabel(tool)} (${n} calls)`;
}

export function getInputSummary(
  tool: string,
  input: Record<string, any> | null | undefined,
): string {
  if (!input) return '';
  if (input.title) return input.title;
  if (input.name) return input.name;
  if (input.query) return `"${input.query}"`;
  if (input.asset_id) return `Asset ${String(input.asset_id).slice(0, 8)}...`;
  if (input.asset_ids) return `${input.asset_ids.length} assets`;
  return '';
}

export function getResultSummary(
  tool: string,
  result: Record<string, any> | null | undefined,
): string {
  if (!result) return 'Completed';
  if (result.share_id) return `Share: ${result.title || result.share_id}`;
  if (result.collection_id) return `Collection: ${result.name || result.collection_id}`;
  if (result.tagged_count) return `Tagged ${result.tagged_count} assets`;
  if (tool === 'tag_assets_bulk' && result.success_count != null) {
    const done = Number(result.success_count);
    const total = Number(result.total_count ?? done);
    const noun = total === 1 ? 'asset' : 'assets';
    return done === total ? `Tagged ${total} ${noun}` : `Tagged ${done} of ${total} ${noun}`;
  }
  if (result.total_count != null) return `Found ${result.total_count} results`;
  if (result.total != null) return `Found ${result.total} results`;
  if (result.asset_id) return `Asset processed`;
  return 'Completed';
}
